import { Model } from "./model";
import { CollectionDescriptor } from "./collection";
import { ReferenceDescriptor } from "./reference";

interface SetupOptions {
  collections?: string[];
  references?: string[];
}

export type ModelMap = Record<string, Model<any> | Array<Model<any>>>;

/**
 * Sets up collections and references for multiple loaded models based on their model configurations.
 * This utility helps avoid repetitive collection/reference setup code when loading multiple related models.
 *
 * It analyzes the model configurations to match up collections and references based on their foreign keys.
 * For example, if TournamentPlayer has a collection of registrations with foreign key 'tournament_player_id',
 * and we have an array of TournamentRegistration models, it will automatically distribute those
 * registrations to the appropriate players based on the foreign key relationship.
 *
 * @param models Object containing the loaded models as key-value pairs
 * @param options Optional configuration to specify which collections/references to load
 *
 * @example
 * ```typescript
 * // After loading players and registrations:
 * setupModelRelations({
 *   players,    // Array of TournamentPlayer models
 *   registrations  // Array of TournamentRegistration models
 * });
 * // This will automatically set up the registrations collection on each player
 * // based on the tournament_player_id foreign key defined in the model config
 * ```
 */
export function setupModelRelations(models: ModelMap, options?: SetupOptions): void {
  // Create a map of model arrays by their type, using first item to determine type
  const modelArrays = new Map<string, { models: Model<any>[]; type: typeof Model }>();

  Object.entries(models).forEach(([_, value]) => {
    const modelArray = Array.isArray(value) ? value : [value];
    if (modelArray.length === 0) return;

    const firstModel = modelArray[0];
    modelArrays.set(firstModel.tableName, {
      models: modelArray,
      type: firstModel.constructor as typeof Model,
    });
  });

  // For each array of models
  modelArrays.forEach(({ models, type: modelType }, tableName) => {
    // Process collections
    setupCollections(models, modelType, modelArrays, options);
    // Process references
    setupReferences(models, modelType, modelArrays, options);
  });
}

/**
 * Sets up collections for a set of models
 */
function setupCollections(
  models: Model<any>[],
  modelType: typeof Model,
  modelArrays: Map<string, { models: Model<any>[]; type: typeof Model }>,
  options?: SetupOptions,
): void {
  // Check if this type of model has any collections
  const collections = modelType.config.collections;
  if (!collections || Object.keys(collections).length === 0) return;

  // For each collection defined in the model
  Object.entries(collections).forEach(([collectionName, descriptor]) => {
    // Skip if options specify collections and this one isn't included
    if (options?.collections && !options.collections.includes(collectionName)) {
      return;
    }

    // Get the target model type for this collection
    const targetModelClass = (descriptor as CollectionDescriptor<any>).model();
    const targetModels = modelArrays.get(targetModelClass.tableName)?.models;
    if (!targetModels) return;

    // Create an index of target models by their foreign key for O(1) lookup
    const targetModelIndex = new Map<string, Model<any>[]>();
    targetModels.forEach((targetModel) => {
      const foreignKeyValue = targetModel.readAttribute((descriptor as CollectionDescriptor<any>).key);
      if (!foreignKeyValue) return;

      const key = String(foreignKeyValue);
      if (!targetModelIndex.has(key)) {
        targetModelIndex.set(key, []);
      }
      targetModelIndex.get(key)!.push(targetModel);
    });

    // For each model, find its related models using the index
    models.forEach((model) => {
      const collection = model.readCollection(collectionName);
      const relatedModels = targetModelIndex.get(model.id!) || [];
      // Always set the collection, even if empty, to mark it as loaded
      // This ensures collections are updated with the latest data from subscriptions
      collection.set(relatedModels);
    });
  });
}

/**
 * Sets up references for a set of models
 */
function setupReferences(
  models: Model<any>[],
  modelType: typeof Model,
  modelArrays: Map<string, { models: Model<any>[]; type: typeof Model }>,
  options?: SetupOptions,
): void {
  // Check if this type of model has any references
  const references = modelType.config.references;
  if (!references || Object.keys(references).length === 0) return;

  // For each reference defined in the model
  Object.entries(references).forEach(([referenceName, descriptor]) => {
    // Skip if options specify references and this one isn't included
    if (options?.references && !options.references.includes(referenceName)) {
      return;
    }

    // Group models by their reference key values
    const idMap = new Map<string, Model<any>[]>();
    models.forEach((model) => {
      if (!model.readReference(referenceName)?.loaded) {
        const id = model.readAttribute((descriptor as ReferenceDescriptor<any>).key);
        if (!id) return;

        const key = String(id);
        if (!idMap.has(key)) {
          idMap.set(key, []);
        }
        idMap.get(key)!.push(model);
      }
    });

    // Get the referenced models
    const targetModelClass = (descriptor as ReferenceDescriptor<any>).model();
    const targetModels = modelArrays.get(targetModelClass.tableName)?.models;
    if (!targetModels) return;

    // Create an index of target models by their id for O(1) lookup
    const targetModelIndex = new Map<string, Model<any>>();
    targetModels.forEach((targetModel) => {
      if (targetModel.id) {
        targetModelIndex.set(targetModel.id, targetModel);
      }
    });

    // For each group of models with the same reference key,
    // find the referenced model and set it
    idMap.forEach((modelsForId, id) => {
      const referencedModel = targetModelIndex.get(id);
      if (referencedModel) {
        modelsForId.forEach((model) => {
          model.readReference(referenceName)?.set(referencedModel);
        });
      }
    });
  });
}
